import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { RefreshCw, CheckCircle2, XCircle, Clock, Calendar, ChevronLeft, ChevronRight, UserCheck } from "lucide-react";
import { useLocation } from "react-router-dom";
import AttendanceModal from "../../components/AttendanceModal";
import { getApiUrl } from "../../config/api";
import "../../styles/DashboardLayout.css";
import "../../styles/UtopiaPages.css";

interface TodayAttendanceRecord {
  id: string;
  studentId: string;
  studentName: string;
  studentEmail: string;
  className: string;
  status: string;
  date: string;
}

interface ClassStudent {
  id: string;
  name: string;
  email: string;
}

const AttendanceRegister: React.FC = () => {
  const [todayAttendance, setTodayAttendance] = useState<TodayAttendanceRecord[]>([]);
  const [classStudents, setClassStudents] = useState<ClassStudent[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [currentPage, setCurrentPage] = useState(1);
  const [classes, setClasses] = useState<any[]>([]);
  const [selectedClassId, setSelectedClassId] = useState<string>("");
  const [selectedDate, setSelectedDate] = useState<string>(
    new Date().toISOString().split('T')[0]
  );
  const itemsPerPage = 8;
  const location = useLocation();

  const token = localStorage.getItem("token");
  const role = (localStorage.getItem("role") || "").toLowerCase();
  const canPickClasses = ["admin", "teacher", "headteacher", "superadmin"].includes(role);
  const requestedClassId = new URLSearchParams(location.search).get("classId") || "";

  const fetchClasses = useCallback(async () => {
    if (!canPickClasses || !token) return;
    try {
      let nextClasses: any[] = [];

      if (role === "teacher") {
        try {
          const teacherRes = await axios.get(getApiUrl("teacher/dashboard"), {
            headers: { Authorization: `Bearer ${token}` }
          });
          nextClasses = teacherRes.data.classes || [];
        } catch {
          const res = await axios.get(getApiUrl("academics/classes"), {
            headers: { Authorization: `Bearer ${token}` }
          });
          const allClasses = res.data.classes || (Array.isArray(res.data) ? res.data : []);
          const currentUserId = localStorage.getItem("userId");
          nextClasses = currentUserId
            ? allClasses.filter((cls: any) =>
                String(cls.teacherId || cls.teacher?.id || "") === String(currentUserId) ||
                String(cls.assistantTeacherId || cls.assistantTeacher?.id || "") === String(currentUserId)
              )
            : [];
        }
      } else {
        const res = await axios.get(getApiUrl("academics/classes"), {
          headers: { Authorization: `Bearer ${token}` }
        });
        nextClasses = res.data.classes || (Array.isArray(res.data) ? res.data : []);
      }

      setClasses(nextClasses);
      const matchedRequestedClass = requestedClassId
        ? nextClasses.find((cls: any) => String(cls.id || cls._id) === requestedClassId)
        : null;
      if (matchedRequestedClass) {
        setSelectedClassId(String(matchedRequestedClass.id || matchedRequestedClass._id));
      } else if (!selectedClassId && nextClasses.length > 0) {
        setSelectedClassId(String(nextClasses[0].id || nextClasses[0]._id));
      }
    } catch (err) {
      console.error("Error fetching classes:", err);
    }
  }, [token, canPickClasses, selectedClassId, requestedClassId, role]);

  const fetchClassStudents = useCallback(async () => {
    if (!token || !selectedClassId) {
      setClassStudents([]);
      return;
    }

    try {
      const endpoint = role === "teacher"
        ? `teacher/classes/${selectedClassId}/students`
        : `academics/classes/${selectedClassId}/students`;
      const res = await axios.get(getApiUrl(endpoint), {
        headers: { Authorization: `Bearer ${token}` }
      });
      setClassStudents(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      console.error("Error fetching class students:", err);
      setClassStudents([]);
    }
  }, [token, selectedClassId, role]);

  const fetchTodayAttendance = useCallback(async () => {
    setLoading(true);
    try {
      let url = `attendance?date=${selectedDate}`;
      if (selectedClassId) url += `&classId=${selectedClassId}`;

      const res = await axios.get(
        getApiUrl(url),
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setTodayAttendance(res.data.records || []);
    } catch (err) {
      console.error("Error fetching today's attendance:", err);
      setTodayAttendance([]);
    } finally {
      setLoading(false);
    }
  }, [token, selectedClassId, selectedDate]);

  useEffect(() => {
    if (token) {
      fetchClasses();
    }
  }, [fetchClasses, token]);

  useEffect(() => {
    if (token && role) {
      fetchTodayAttendance();
    }
  }, [fetchTodayAttendance, token, role]);

  useEffect(() => {
    if (token && selectedClassId) {
      fetchClassStudents();
    }
  }, [fetchClassStudents, token, selectedClassId]);

  const attendanceRows = React.useMemo(() => {
    if (!selectedClassId) {
      return todayAttendance;
    }

    const selectedClass = classes.find((cls) => String(cls.id || cls._id) === selectedClassId);
    const selectedClassName = selectedClass?.name || "";
    const attendanceMap = new Map(todayAttendance.map((record) => [String(record.studentId), record]));

    return classStudents.map((student) => {
      const existing = attendanceMap.get(String(student.id));
      return {
        id: existing?.id || `${selectedClassId}-${student.id}`,
        studentId: String(student.id),
        studentName: existing?.studentName || student.name,
        studentEmail: existing?.studentEmail || student.email,
        className: existing?.className || selectedClassName,
        status: existing?.status || "not_marked",
        date: existing?.date || selectedDate
      };
    });
  }, [selectedClassId, todayAttendance, classStudents, classes, selectedDate]);

  const filteredAttendance = attendanceRows.filter((record) => {
    const matchesSearch =
      record.studentName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      record.studentEmail.toLowerCase().includes(searchTerm.toLowerCase()) ||
      record.className.toLowerCase().includes(searchTerm.toLowerCase());

    if (statusFilter === "all") return matchesSearch;
    if (statusFilter === "present") return matchesSearch && record.status === "present";
    if (statusFilter === "absent") return matchesSearch && record.status === "absent";
    if (statusFilter === "excused") return matchesSearch && record.status === "absent_with_permission";
    if (statusFilter === "not_marked") return matchesSearch && record.status === "not_marked";

    return matchesSearch;
  });

  useEffect(() => {
    setCurrentPage(1);
  }, [statusFilter, searchTerm]);

  const totalPages = Math.ceil(filteredAttendance.length / itemsPerPage);
  const currentAttendance = filteredAttendance.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);
  const stats = {
    present: filteredAttendance.filter((record) => record.status === "present").length,
    absent: filteredAttendance.filter((record) => record.status === "absent").length,
    excused: filteredAttendance.filter((record) => record.status === "absent_with_permission").length
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case "present":
        return <span className="utopia-badge bg-teal-50 text-teal-600 border-teal-100 uppercase text-[9px] font-black tracking-tighter">Present</span>;
      case "absent":
        return <span className="utopia-badge bg-red-50 text-red-500 border-red-100 uppercase text-[9px] font-black tracking-tighter">Absent</span>;
      case "absent_with_permission":
        return <span className="utopia-badge bg-amber-50 text-amber-600 border-amber-100 uppercase text-[9px] font-black tracking-tighter">Excused</span>;
      case "not_marked":
        return <span className="utopia-badge bg-slate-50 text-slate-500 border-slate-100 uppercase text-[9px] font-black tracking-tighter">Not Marked</span>;
      default:
        return <span className="utopia-badge bg-slate-50 text-slate-500 border-slate-100 uppercase text-[9px] font-black tracking-tighter">{status}</span>;
    }
  };

  return (
    <div className="dashboard-container">
      <main className="dashboard-main">

        <div className="utopia-page-container overflow-hidden border border-slate-100 rounded-3xl shadow-sm bg-white">
          <div className="px-8 pt-6 pb-4 border-b border-slate-100 flex flex-wrap items-center justify-between gap-4">
            <h1 className="text-2xl font-black text-slate-800">Attendance Registry</h1>
            <div className="flex flex-nowrap items-center gap-2 overflow-x-auto">
              <div className="px-2 py-1.5 rounded-md bg-teal-50/60 border border-teal-100 flex items-center gap-2 min-w-[160px]">
                <div className="w-6 h-6 rounded-md bg-white flex items-center justify-center text-teal-600 shadow-sm"><CheckCircle2 size={12} /></div>
                <div className="flex items-center justify-between gap-2 flex-1">
                  <div className="text-[9px] font-bold text-slate-600 uppercase tracking-wider">Present</div>
                  <div className="text-sm font-black text-teal-600">{stats.present}</div>
                </div>
              </div>
              <div className="px-2 py-1.5 rounded-md bg-rose-50/60 border border-rose-100 flex items-center gap-2 min-w-[160px]">
                <div className="w-6 h-6 rounded-md bg-white flex items-center justify-center text-rose-600 shadow-sm"><XCircle size={12} /></div>
                <div className="flex items-center justify-between gap-2 flex-1">
                  <div className="text-[9px] font-bold text-slate-600 uppercase tracking-wider">Absent</div>
                  <div className="text-sm font-black text-rose-600">{stats.absent}</div>
                </div>
              </div>
              <div className="px-2 py-1.5 rounded-md bg-amber-50/60 border border-amber-100 flex items-center gap-2 min-w-[160px]">
                <div className="w-6 h-6 rounded-md bg-white flex items-center justify-center text-amber-600 shadow-sm"><Clock size={12} /></div>
                <div className="flex items-center justify-between gap-2 flex-1">
                  <div className="text-[9px] font-bold text-slate-600 uppercase tracking-wider">Excused</div>
                  <div className="text-sm font-black text-amber-600">{stats.excused}</div>
                </div>
              </div>
            </div>
          </div>
          <div className="px-2 py-4 border-b border-slate-100">
            <AttendanceModal
              embedded
              onClose={fetchTodayAttendance}
              selectedClassId={selectedClassId}
              onSelectedClassChange={setSelectedClassId}
              selectedDate={selectedDate}
              onSelectedDateChange={setSelectedDate}
            />
          </div>

          <div className="utopia-table-header px-8 pt-8 flex items-center justify-between gap-4">
            <h2>Participation Records</h2>
            <div className="flex items-center gap-3">
              <label className="text-xs font-bold text-slate-600 uppercase tracking-wider">Class</label>
              <select
                className="utopia-input min-h-[44px] min-w-[180px] text-sm font-semibold bg-white border-2 border-slate-200 px-3 py-2 leading-normal"
                value={selectedClassId}
                onChange={(e) => setSelectedClassId(e.target.value)}
              >
                <option value="">Select Class</option>
                {classes.map((cls) => (
                  <option key={cls.id || cls._id} value={cls.id || cls._id}>
                    {cls.name}
                  </option>
                ))}
              </select>
              <label className="text-xs font-bold text-slate-600 uppercase tracking-wider">Date</label>
              <input
                type="date"
                className="utopia-input min-h-[44px] text-sm font-semibold bg-white border-2 border-slate-200 px-3 py-2 leading-normal"
                value={selectedDate}
                onChange={(e) => setSelectedDate(e.target.value)}
              />
              <label className="text-xs font-bold text-slate-600 uppercase tracking-wider">Status</label>
              <select
                className="utopia-input min-h-[44px] text-sm font-semibold bg-white border-2 border-slate-200 px-3 py-2 leading-normal"
                value={statusFilter}
                onChange={e => setStatusFilter(e.target.value)}
              >
                <option value="all">All Status</option>
                <option value="present">Present</option>
                <option value="absent">Absent</option>
                <option value="excused">Excused</option>
                <option value="not_marked">Not Marked</option>
              </select>
            </div>
          </div>

          {loading ? (
            <div className="p-20 text-center"><RefreshCw className="animate-spin text-slate-300 mx-auto" size={40} /></div>
          ) : !selectedClassId ? (
            <div className="p-24 text-center text-slate-400">
              <UserCheck size={64} className="mx-auto mb-4 opacity-5" />
              <p className="font-bold text-slate-600">Select a class to view learners for attendance.</p>
            </div>
          ) : filteredAttendance.length === 0 ? (
            <div className="p-32 text-center text-slate-400">
              <UserCheck size={64} className="mx-auto mb-4 opacity-5" />
              <p className="font-bold text-slate-600">
                No learners match the current class, date, and status filters.
              </p>
            </div>
          ) : (
            <>
              <table className="utopia-table">
                <thead>
                  <tr>
                    <th className="text-slate-700">Student Identity</th>
                    <th className="text-slate-700">Email Contact</th>
                    <th className="text-slate-700">Academic Class</th>
                    <th className="text-slate-700">Participation State</th>
                  </tr>
                </thead>
                <tbody>
                  {currentAttendance.map((record) => (
                    <tr key={record.id}>
                      <td>
                        <div className="flex items-center gap-3">
                          <div className="w-9 h-9 rounded-xl bg-slate-50 flex items-center justify-center text-slate-500 font-bold text-xs uppercase shadow-inner">
                            {record.studentName.charAt(0)}
                          </div>
                          <span className="font-bold text-slate-800">{record.studentName}</span>
                        </div>
                      </td>
                      <td className="text-slate-600 text-sm">{record.studentEmail}</td>
                      <td>
                        <span className="utopia-badge bg-blue-50 text-blue-700 border border-blue-200 text-[10px] font-bold">
                          {record.className}
                        </span>
                      </td>
                      <td>{getStatusBadge(record.status)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {totalPages > 1 && (
                <div className="utopia-pagination">
                  <button className="utopia-page-btn" disabled={currentPage === 1} onClick={() => setCurrentPage(p => p - 1)}><ChevronLeft size={16} /></button>
                  <span className="text-xs font-black text-slate-600 mx-4">Entry {currentPage} of {totalPages}</span>
                  <button className="utopia-page-btn" disabled={currentPage === totalPages} onClick={() => setCurrentPage(p => p + 1)}><ChevronRight size={16} /></button>
                </div>
              )}
            </>
          )}
        </div>
      </main>

    </div>
  );
};

export default AttendanceRegister;
